import { Alert, Button as AntButton, Input, Segmented, Select, Space, Table, Tabs, Tag } from 'antd';
import type { TableProps } from 'antd';
import { Building2, Users } from 'lucide-react';
import { useState } from 'react';
import { DatePicker } from '@/components/DatePicker';
import { EmptyState } from '@/components/EmptyState';
import { HierarchyTable, type HierarchyRow } from '@/components/HierarchyTable';
import { ReorderableList } from '@/components/ReorderableList';
import { RequirementPreview } from '@/components/RequirementPreview';
import { NameCell } from '@/components/NameCell';
import { PageHeader } from '@/components/PageHeader';
import { FilterField } from '@/components/FilterField';
import { StatusDot } from '@/components/StatusDot';
import { StatusPill } from '@/components/StatusPill';
import { TwoColumnLayout } from '@/components/TwoColumnLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { useTreeExpansion } from '@/hooks/useTreeExpansion';
import { expandableKeysOf, keysToDepth } from '@/lib/orgTree';
import { themeShape, type ThemeMode } from '@/theme/tokens';
import type { StatusTone } from '@/theme/statusTone';

/**
 * EVERY TOKEN, EVERY PRIMITIVE, EVERY RESTYLED ANTD COMPONENT — rendered once.
 *
 * The gallery mounts this twice, inside two `ThemeScope`s, so light and dark sit side by
 * side on one page. Nothing here reads the app's theme: everything resolves against the
 * variables its scope wrote, which is the property the arrangement is there to prove.
 */

const Section = ({ title, note, children }: { title: string; note?: string; children: React.ReactNode }) => (
  <section className="space-y-3">
    <div>
      <h3 className="text-sm font-semibold tracking-tight">{title}</h3>
      {note && <p className="mt-0.5 text-xs text-muted-foreground">{note}</p>}
    </div>
    {children}
  </section>
);

/**
 * [P2] Fixtures for the two policy-builder idioms below. Declared at module scope so the
 * two `ThemeScope` mounts of this gallery share them rather than each building their own.
 */
const DEMO_POSTS = new Map([
  ['pt1', { id: 'pt1', name: 'Sub-Inspector / Sergeant / Subedar', shortName: 'SI/Sgt/Sub', archivedAt: null }],
  ['pt2', { id: 'pt2', name: 'Assistant Sub-Inspector / A-ASI', shortName: 'ASI/A-ASI', archivedAt: null }],
  ['pt3', { id: 'pt3', name: 'Unarmed Men', shortName: 'UB Men', archivedAt: null }],
  ['pt4', { id: 'pt4', name: 'Women Police', shortName: 'Women Police', archivedAt: null }],
]);

const DEMO_VEHICLES = new Map([
  ['v1', { id: 'v1', name: 'Any vehicle', code: 'ANY', isWildcard: true, archivedAt: null }],
]);

const SURFACE_SWATCHES = [
  { label: 'background', className: 'bg-background' },
  { label: 'card', className: 'bg-card' },
  { label: 'surface-raised', className: 'bg-surface-raised' },
  { label: 'hover', className: 'bg-hover' },
  { label: 'active', className: 'bg-active' },
  { label: 'primary', className: 'bg-primary' },
  { label: 'secondary', className: 'bg-secondary' },
  { label: 'destructive', className: 'bg-destructive' },
  { label: 'success', className: 'bg-success' },
  { label: 'warning', className: 'bg-warning' },
  { label: 'info', className: 'bg-info' },
] as const;

const TEXT_STEPS = [
  { label: 'foreground · 92%', className: 'text-foreground' },
  { label: 'muted-foreground · 56%', className: 'text-muted-foreground' },
  { label: 'subtle · 38% — decorative only', className: 'text-subtle' },
  { label: 'link', className: 'text-link underline underline-offset-4' },
] as const;

const RADII = [
  { label: 'sm · 4 — micro chips', className: 'rounded-sm' },
  { label: 'md · 6 — small controls, tags', className: 'rounded-md' },
  { label: 'lg · 8 — default controls, cards', className: 'rounded-lg' },
  { label: 'xl · 10 — modals, drawers, tables', className: 'rounded-xl' },
  { label: 'full — pills, avatars, dots', className: 'rounded-full' },
] as const;

const TONES: StatusTone[] = ['success', 'warning', 'danger', 'info', 'neutral'];

type SystemRow = { key: string; system: string; role: string; layer: string };

const SYSTEM_ROWS: SystemRow[] = [
  { key: 'preflight', system: 'Tailwind preflight', role: 'CSS reset', layer: 'base' },
  { key: 'antd', system: 'Ant Design 5', role: 'Data-dense controls', layer: 'antd' },
  { key: 'shadcn', system: 'shadcn/ui', role: 'Shell and primitives', layer: 'components' },
  { key: 'tailwind', system: 'Tailwind utilities', role: 'Layout and overrides', layer: 'utilities' },
];

const SYSTEM_COLUMNS: TableProps<SystemRow>['columns'] = [
  { title: 'System', dataIndex: 'system', key: 'system', sorter: (a, b) => a.system.localeCompare(b.system) },
  { title: 'Role', dataIndex: 'role', key: 'role' },
  { title: 'CSS layer', dataIndex: 'layer', key: 'layer', render: (layer: string) => <code>@layer {layer}</code> },
];

type TreeRow = HierarchyRow & { code: string; name: string; officers: number; children?: TreeRow[] };

const TREE_ROWS: TreeRow[] = [
  {
    key: 'kp',
    code: 'KP',
    name: 'Kolkata Police',
    officers: 120,
    treeDepth: 0,
    isLastChild: true,
    children: [
      {
        key: 'south',
        code: 'SOUTHDIV',
        name: 'South Division',
        officers: 55,
        treeDepth: 1,
        parentKey: 'kp',
        isLastChild: false,
        children: [
          {
            key: 'tolly',
            code: 'TOLLYPS',
            name: 'Tollygunge PS',
            officers: 22,
            treeDepth: 2,
            parentKey: 'south',
            isLastChild: true,
            children: [
              {
                key: 'op',
                code: 'TOLLYOP',
                name: 'Tollygunge Outpost',
                officers: 9,
                treeDepth: 3,
                parentKey: 'tolly',
                isLastChild: true,
                children: [
                  {
                    key: 'beat',
                    code: 'TOLLYB3',
                    name: 'Tollygunge Beat 3',
                    officers: 4,
                    treeDepth: 4,
                    parentKey: 'op',
                    isLastChild: true,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        key: 'north',
        code: 'NORTHDIV',
        name: 'North Division',
        officers: 40,
        treeDepth: 1,
        parentKey: 'kp',
        isLastChild: true,
      },
    ],
  },
];

export const Gallery = ({ mode }: { mode: ThemeMode }) => {
  const [segment, setSegment] = useState<string | number>('Tree');
  /**
   * [P2] Local, because the reorder demo is meant to be reordered. ⚠️ The gallery is
   * mounted TWICE (one `ThemeScope` per theme), so this state is per-mount — which is
   * correct here, and is also why `ReorderableList` is given no `data-testid` in this
   * file: two mounts would produce two of it.
   */
  const [reorderDemo, setReorderDemo] = useState([
    { id: 'pt1', label: 'SI/Sgt/Sub', content: 'Sub-Inspector / Sergeant / Subedar' },
    { id: 'pt2', label: 'ASI/A-ASI', content: 'Assistant Sub-Inspector / A-ASI' },
    { id: 'pt3', label: 'UB Men', content: 'Unarmed Men' },
    { id: 'pt4', label: 'Women Police', content: 'Women Police' },
  ]);
  const expansion = useTreeExpansion({
    expandableKeys: expandableKeysOf(TREE_ROWS as never),
    autoKeys: keysToDepth(TREE_ROWS as never, 2),
    boundedKeys: keysToDepth(TREE_ROWS as never, 3),
    urlKey: `expand-${mode}`,
  });

  return (
    <div className="space-y-8 p-6">
      <Section title="Surfaces" note="Elevation is surface lightness plus a hairline border. No shadows in dark.">
        <div className="flex flex-wrap gap-3">
          {SURFACE_SWATCHES.map((swatch) => (
            <div key={swatch.label} className="w-36">
              <div className={`h-12 rounded-lg border border-border ${swatch.className}`} />
              <p className="mt-1 truncate font-mono text-[11px] text-muted-foreground">{swatch.label}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Text ramp" note="Tertiary is decorative: it does not clear 4.5:1 and is not meant to.">
        <div className="space-y-1">
          {TEXT_STEPS.map((step) => (
            <p key={step.label} className={`text-sm ${step.className}`}>
              {step.label} — The quick brown fox jumps over the lazy dog.
            </p>
          ))}
        </div>
      </Section>

      <Section title="Borders" note="Two weights only: subtle for dividers, strong for control boundaries.">
        <div className="flex flex-wrap gap-3">
          <div className="h-12 w-40 rounded-lg border border-border-subtle bg-card p-2 text-[11px] text-muted-foreground">
            border-subtle
          </div>
          <div className="h-12 w-40 rounded-lg border border-border-strong bg-card p-2 text-[11px] text-muted-foreground">
            border-strong
          </div>
        </div>
      </Section>

      <Section title="Radius" note="Nothing reaches 16. A card radius above 12 is the loudest template tell.">
        <div className="flex flex-wrap gap-3">
          {RADII.map((radius) => (
            <div key={radius.label} className="w-44">
              <div className={`h-12 border border-border-strong bg-card ${radius.className}`} />
              <p className="mt-1 truncate text-[11px] text-muted-foreground">{radius.label}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Type" note={`One family: ${themeShape.fontFamily.split(',')[0]}. Mono is for code chips only.`}>
        <div className="space-y-1">
          <p className="text-xl font-semibold tracking-tight">Page title · 20/600</p>
          <p className="text-sm">Body · 14/400 — the base size AntD and Tailwind share.</p>
          <p className="font-mono text-xs text-muted-foreground">TOLLYPS · code chip, 12/mono</p>
        </div>
      </Section>

      <Separator />

      <Section title="Status" note="Dots in lists, one pill on a detail record. A list of pills means nothing.">
        <div className="flex flex-wrap items-center gap-4">
          {TONES.map((tone) => (
            <StatusDot key={tone} tone={tone} label={tone} />
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {TONES.map((tone) => (
            <StatusPill key={tone} tone={tone}>
              {tone}
            </StatusPill>
          ))}
        </div>
      </Section>

      <Section title="Buttons" note="shadcn on the left, AntD on the right. Same heights, same radius, same tokens.">
        <div className="flex flex-wrap items-center gap-2">
          <Button>Primary</Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="outline">Outline</Button>
          <Button variant="ghost">Ghost</Button>
          <Button variant="destructive">Destructive</Button>
          <Button variant="link">Link</Button>
        </div>
        <Space wrap>
          <AntButton type="primary">Primary</AntButton>
          <AntButton>Default</AntButton>
          <AntButton type="text">Text</AntButton>
          <AntButton type="link">Link</AntButton>
          <AntButton danger>Danger</AntButton>
          <AntButton disabled>Disabled</AntButton>
        </Space>
      </Section>

      <Section title="Controls" note="Every one of these reads its height, radius and colours from the token layer.">
        <div className="flex flex-wrap items-end gap-3">
          <FilterField label="Search" className="w-56">
            <Input placeholder="Name or code" allowClear />
          </FilterField>
          <FilterField label="Type" className="w-44">
            <Select
              className="w-full"
              placeholder="Any"
              options={[
                { value: 'COMMISSIONERATE', label: 'Commissionerate' },
                { value: 'DIVISION', label: 'Division' },
                { value: 'UNIT', label: 'Unit' },
              ]}
            />
          </FilterField>
          <FilterField label="Effective from" className="w-52">
            <DatePicker className="w-full" />
          </FilterField>
        </div>
        <Segmented value={segment} onChange={setSegment} options={['Tree', 'List']} />
        <div className="flex flex-wrap gap-2">
          <Tag>default</Tag>
          <Tag color="success">success</Tag>
          <Tag color="warning">warning</Tag>
          <Tag color="error">error</Tag>
        </div>
      </Section>

      <Section title="Tabs">
        <Tabs
          items={[
            { key: 'child-units', label: 'Child units', children: <p className="text-sm">Child units panel.</p> },
            { key: 'officers', label: 'Officers', children: <p className="text-sm">Officers panel.</p> },
            { key: 'history', label: 'Posting history', children: <p className="text-sm">History panel.</p> },
          ]}
        />
      </Section>

      <Section title="Alert" note="Used for a page-level warning — see PolicyBuilder, OrgTree and DutyFill.">
        <Alert type="warning" showIcon message="This policy version is published and can no longer be edited." />
      </Section>

      <Separator />

      <Section title="PageHeader" note="Leading muted icon, title, subtitle, right-aligned actions, filter row.">
        <PageHeader
          icon={Building2}
          title="Organisation"
          subtitle="20 units within your scope"
          actions={<AntButton type="primary">Add unit</AntButton>}
          filters={
            <FilterField label="Status" className="w-44">
              <Select className="w-full" defaultValue="active" options={[{ value: 'active', label: 'Active' }]} />
            </FilterField>
          }
        />
      </Section>

      <Section
        title="HierarchyTable"
        note="Five levels. Dotted guides connect parent to child at any depth; the data columns stay aligned."
      >
        <HierarchyTable<TreeRow>
          rows={TREE_ROWS}
          ariaLabel={`Example hierarchy (${mode})`}
          expansion={expansion}
          tree={{ title: 'Unit', code: (row) => row.code, name: (row) => row.name }}
          columns={[
            { key: 'officers', title: 'Officers', dataIndex: 'officers', align: 'right', width: 96 },
            {
              key: 'status',
              title: 'Status',
              width: 120,
              render: () => <StatusDot tone="success" label="Active" />,
            },
          ]}
          rowActions={(row) => [
            { key: 'open', label: `Open ${row.code}` },
            { key: 'move', label: 'Move…' },
          ]}
        />
      </Section>

      <Section title="AntD Table" note="Transparent header, hairline separators, no zebra, no grey band.">
        <Table<SystemRow> rowKey="key" dataSource={SYSTEM_ROWS} columns={SYSTEM_COLUMNS} pagination={false} />
      </Section>

      <Section title="TwoColumnLayout · detail" note="Narrow identity column, wide substance column.">
        {/*
          ⚠️ [C3a §4] A TEST HANDLE, BECAUSE THIS GALLERY IS NOW THE VARIANT'S ONLY CONSUMER.

          `pages/ArrangementDetail` was the other one, and §4 hid it behind
          `SURFACES.arrangementRecordPage` — so `e2e/specs/collapse.spec.ts`'s "anchors detail to
          the TOP" arm, which is the only browser assertion [UX-FINAL]'s anchor rule has, lost the
          route it was made on. It is re-pointed here, and it needs a scope because this page
          renders both variants and `[data-slot="left"]` would otherwise match twice.
        */}
        <div data-testid="gallery-two-column-detail">
          <TwoColumnLayout
            variant="detail"
            left={
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Tollygunge PS</CardTitle>
                  <CardDescription>UNIT · PS</CardDescription>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground">Code, type, parent, status, counts.</CardContent>
              </Card>
            }
            right={
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Officers posted here</CardTitle>
                </CardHeader>
                <CardContent>
                  <EmptyState title="No officers posted to this unit." size="sm" />
                </CardContent>
              </Card>
            }
          />
        </div>
      </Section>

      <Section title="TwoColumnLayout · form" note="The mirror: wide fields, narrow sticky summary.">
        <TwoColumnLayout
          variant="form"
          left={
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Unit details</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-3">
                <FilterField label="Name">
                  <Input placeholder="Tollygunge PS" />
                </FilterField>
                <FilterField label="Short code">
                  <Input placeholder="TOLLYPS" />
                </FilterField>
              </CardContent>
            </Card>
          }
          right={
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Summary</CardTitle>
                <CardDescription>Updates as you type.</CardDescription>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                <Users className="mb-1 size-4" aria-hidden />
                Nothing entered yet.
              </CardContent>
            </Card>
          }
        />
      </Section>

      <Section title="EmptyState" note="A dashed box and one sentence. No illustration, no icon.">
        <EmptyState
          title="No org units in your scope yet."
          description="A super admin creates the first unit; everything else hangs beneath it."
        />
      </Section>

      <Section
        title="Near-duplicate prompt"
        note="[P1b] A warning Alert, not a blocking dialog. The prompt is the point; creating anyway is always allowed."
      >
        <Alert
          type="warning"
          showIcon
          message="Did you mean one of these?"
          description={
            <div className="flex flex-col gap-2">
              <ul className="m-0 list-none space-y-1 p-0">
                <li className="text-sm">
                  <span className="font-medium">Esplanade</span>
                  <span className="text-muted-foreground"> — Chowringhee, Kolkata</span>
                </li>
              </ul>
              <p className="m-0 text-xs text-muted-foreground">
                Use an existing place where you can. Creating this one anyway is still allowed.
              </p>
            </div>
          }
        />
      </Section>

      <Section
        title="Alias cell"
        note="[P1b] Alternative names, muted and inline. A merge folds a name in here, so a place that has absorbed another reads as one record with a history."
      >
        <div className="flex min-w-0 flex-col">
          <span className="truncate">Esplanade</span>
          <span className="truncate text-xs text-muted-foreground">also: Chowringhee · Esplanade Crossing</span>
        </div>
      </Section>

      <Section
        title="Name cell — the first column is the way in"
        note="[P2] Clicking a row's name OPENS that row. It opens rather than edits: clickable for every role, read-only without the write capability, with the write action left in the row and disabled. Anything that NAVIGATES renders a real <a> so ⌘-click and copy-link work; anything that opens a panel in place renders a button. The muted second line sits outside the control, or every row's accessible name would carry it."
      >
        <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-3">
          <NameCell name="Standard PA" to="/policies/demo" secondary="The everyday arrangement." />
          <NameCell name="SI / Sgt. / Subedar" onOpen={() => undefined} secondary="SI/Sgt/Sub" />
          <NameCell name="Never versioned" />
        </div>
      </Section>

      <Section
        title="Reorderable row list"
        note="[P2] Two paths over one onReorder: drag for a pointer, Move up / Move down for a keyboard or a touchscreen. The buttons are not a fallback — drag is not keyboard accessible without a parallel path, so the explicit control has to exist anyway (the same argument that kept drag-to-reparent off the org tree). Focus follows the row that moved; the unavailable direction is disabled, never hidden."
      >
        <ReorderableList
          items={reorderDemo}
          ariaLabel="Post columns (demo)"
          onReorder={setReorderDemo}
          onRemove={(item) => setReorderDemo((rows) => rows.filter((row) => row.id !== item.id))}
        />
      </Section>

      <Section
        title="Requirement preview"
        note="[P2] The column set a CP nodal will see in P3, derived by lib/policyPreview.ts — the same function P3's real grid must use. Post columns come out in the POLICY's order, never the master's; the vehicle block is always last. Command roles are listed OUTSIDE the grid and are never counted (FR-5.2.6b). The em-dashes are deliberate: this previews the column set, not data."
      >
        <RequirementPreview
          postTypeIds={['pt1', 'pt2', 'pt3', 'pt4']}
          postTypes={DEMO_POSTS}
          vehicleTypeIds={['v1']}
          vehicleTypes={DEMO_VEHICLES}
          commandRoles={[
            { label: 'Supervision', isRequired: false },
            { label: 'Incharge', isRequired: true },
            { label: 'Assisting Inspector', isRequired: false },
          ]}
        />
      </Section>
    </div>
  );
};
